import { WEBSOCKET_CONFIG, WEBSOCKET_CLOSE_CODES } from "./config";

export type WebSocketManagerState = "disconnected" | "connecting" | "connected" | "error";

export interface WebSocketManagerEvents {
  connected: () => void;
  disconnected: () => void;
  error: (error: Error, code?: number) => void;
  message: (event: MessageEvent) => void;
  stateChange: (state: WebSocketManagerState) => void;
}

interface ReconnectionState {
  attempts: number;
  delay: number;
  timeoutId: NodeJS.Timeout | null;
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private state: WebSocketManagerState = "disconnected";
  private url: string | null = null;

  private urlProvider: (() => Promise<string>) | null = null;

  private eventListeners: Partial<WebSocketManagerEvents> = {};
  private reconnectionState: ReconnectionState = {
    attempts: 0,
    delay: WEBSOCKET_CONFIG.reconnection.initialDelay,
    timeoutId: null
  };

  constructor() {
    this.handleOpen = this.handleOpen.bind(this);
    this.handleMessage = this.handleMessage.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  // Public API
  setUrlProvider(provider: () => Promise<string>): void {
    this.urlProvider = provider;
  }

  async connectWithProvider(): Promise<void> {
    if (!this.urlProvider) {
      throw new Error("No urlProvider configured for WebSocketManager");
    }
    const url = await this.urlProvider();
    this.connect(url);
  }

  connect(url: string): void {
    if (this.state === "connecting" || this.state === "connected") {
      console.warn("WebSocket is already connecting or connected");
      return;
    }

    this.url = url;
    this.cancelReconnection();
    this.setState("connecting");

    try {
      this.ws = new WebSocket(url);
      this.setupEventListeners();

      // Connection timeout
      const timeoutId = setTimeout(() => {
        if (this.ws?.readyState === WebSocket.CONNECTING) {
          this.ws.close();
          this.handleError(new Error("Connection timeout"));
        }
      }, WEBSOCKET_CONFIG.connection.timeout);

      this.ws.addEventListener(
        "open",
        () => {
          clearTimeout(timeoutId);
        },
        { once: true }
      );
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error("Failed to create WebSocket"));
    }
  }

  disconnect(): void {
    this.cancelReconnection();

    if (
      this.ws &&
      this.ws.readyState !== WebSocket.CLOSED &&
      this.ws.readyState !== WebSocket.CLOSING
    ) {
      this.ws.close(WEBSOCKET_CLOSE_CODES.NORMAL, "User requested disconnect");
    }

    this.ws = null;
    this.setState("disconnected");
  }

  reconnect(): void {
    console.log("Manual reconnection requested");
    this.disconnect();
    this.resetReconnectionState();

    if (this.urlProvider) {
      void this.connectWithProvider();
      return;
    }

    if (!this.url) {
      console.error("Cannot reconnect: no URL stored");
      return;
    }

    this.connect(this.url);
  }

  send(message: string): boolean {
    if (!this.isConnected() || !this.ws) {
      console.warn("Cannot send message: WebSocket is not connected");
      return false;
    }

    try {
      this.ws.send(message);
      return true;
    } catch (error) {
      console.error("Failed to send message:", error);
      return false;
    }
  }

  isConnected(): boolean {
    return this.state === "connected" && this.ws?.readyState === WebSocket.OPEN;
  }

  getState(): WebSocketManagerState {
    return this.state;
  }

  getReconnectionInfo() {
    return {
      attempts: this.reconnectionState.attempts,
      delay: this.reconnectionState.delay,
      isScheduled: this.reconnectionState.timeoutId !== null
    };
  }

  // Event management
  on<K extends keyof WebSocketManagerEvents>(event: K, listener: WebSocketManagerEvents[K]): void {
    this.eventListeners[event] = listener;
  }

  off<K extends keyof WebSocketManagerEvents>(event: K): void {
    delete this.eventListeners[event];
  }

  destroy(): void {
    this.disconnect();
    this.eventListeners = {};
  }

  // Private methods
  private setupEventListeners(): void {
    if (!this.ws) return;

    this.ws.addEventListener("open", this.handleOpen);
    this.ws.addEventListener("message", this.handleMessage);
    this.ws.addEventListener("error", this.handleError);
    this.ws.addEventListener("close", this.handleClose);
  }

  private handleOpen(): void {
    console.log("WebSocket connected");
    this.resetReconnectionState();
    this.setState("connected");
    this.emit("connected");
  }

  private handleMessage(event: MessageEvent): void {
    this.emit("message", event);
  }

  private handleError(error: Event | Error): void {
    const err = error instanceof Error ? error : new Error("WebSocket connection error");
    console.error("WebSocket error event triggered", err);
  }

  private handleClose(event: CloseEvent): void {
    this.ws = null;
    this.setState("disconnected");
    this.emit("disconnected");
    const mutedCodes = [1000, 1001];
    const conditionallyMutedCodes = [1005, 1006];
    const shouldTriggerError =
      !mutedCodes.includes(event.code) &&
      (!conditionallyMutedCodes.includes(event.code) || this.reconnectionState.attempts > 0);

    if (shouldTriggerError) {
      const message = this.getErrorMessageByCode(event.code);
      console.error("WebSocket error event triggered", message);
      const err = new Error(message);
      this.emit("error", err, event.code);
      this.setState("error");
    }

    if (this.shouldAttemptReconnection(event)) {
      this.scheduleReconnection();
    }
  }

  private getErrorMessageByCode(code: number): string {
    switch (code) {
      case 1005:
        return `Error connecting to real-time feed: ${code}`;
      case 1006:
        return `Error connecting to real-time feed: ${code}`;
      case 1008:
        return "Policy Violation: The server rejected the connection.";
      case 1011:
        return "Server Error: The server encountered an unexpected condition.";
      case 1015:
        return "TLS Handshake failure: Secure connection could not be established.";
      default:
        return `Connection closed with code ${code}`;
    }
  }

  private shouldAttemptReconnection(event: CloseEvent): boolean {
    if (!this.urlProvider && !this.url) return false;
    if (event.code === WEBSOCKET_CLOSE_CODES.NORMAL) return false;
    if (event.code === WEBSOCKET_CLOSE_CODES.POLICY_VIOLATION) return false;

    const maxAttempts = WEBSOCKET_CONFIG.reconnection.maxAttempts;
    if (maxAttempts !== Infinity && this.reconnectionState.attempts >= maxAttempts) {
      console.warn("Max reconnection attempts reached");
      return false;
    }

    return true;
  }

  private scheduleReconnection(): void {
    this.cancelReconnection();

    this.reconnectionState.attempts++;
    console.log(
      `Scheduling reconnection attempt ${this.reconnectionState.attempts} in ${this.reconnectionState.delay}ms`
    );

    this.reconnectionState.timeoutId = setTimeout(() => {
      this.reconnectionState.timeoutId = null;

      // Exponential backoff
      this.reconnectionState.delay = Math.min(
        this.reconnectionState.delay * WEBSOCKET_CONFIG.reconnection.backoffMultiplier,
        WEBSOCKET_CONFIG.reconnection.maxDelay
      );

      if (this.urlProvider) {
        void this.connectWithProvider();
        return;
      }

      if (this.url) {
        this.connect(this.url);
      }
    }, this.reconnectionState.delay);
  }

  private cancelReconnection(): void {
    if (this.reconnectionState.timeoutId) {
      clearTimeout(this.reconnectionState.timeoutId);
      this.reconnectionState.timeoutId = null;
    }
  }

  private resetReconnectionState(): void {
    this.cancelReconnection();
    this.reconnectionState.attempts = 0;
    this.reconnectionState.delay = WEBSOCKET_CONFIG.reconnection.initialDelay;
  }

  private setState(newState: WebSocketManagerState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.emit("stateChange", newState);
    }
  }

  private emit<K extends keyof WebSocketManagerEvents>(
    event: K,
    ...args: Parameters<WebSocketManagerEvents[K]>
  ): void {
    const listener = this.eventListeners[event];
    if (!listener) return;

    if (event === "error") {
      const errorListener = listener as WebSocketManagerEvents["error"];
      const [err, code] = args as Parameters<WebSocketManagerEvents["error"]>;
      errorListener(err, code);
    } else {
      switch (event) {
        case "connected":
          (listener as WebSocketManagerEvents["connected"])(
            ...(args as Parameters<WebSocketManagerEvents["connected"]>)
          );
          break;
        case "disconnected":
          (listener as WebSocketManagerEvents["disconnected"])(
            ...(args as Parameters<WebSocketManagerEvents["disconnected"]>)
          );
          break;
        case "error":
          (listener as WebSocketManagerEvents["error"])(
            ...(args as Parameters<WebSocketManagerEvents["error"]>)
          );
          break;
        case "message":
          (listener as WebSocketManagerEvents["message"])(
            ...(args as Parameters<WebSocketManagerEvents["message"]>)
          );
          break;
        case "stateChange":
          (listener as WebSocketManagerEvents["stateChange"])(
            ...(args as Parameters<WebSocketManagerEvents["stateChange"]>)
          );
          break;
      }
    }
  }
}
